import React, { useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { SectionHeader } from "@/components/common/Widgets";
import ContextualUploader from "@/components/common/ContextualUploader";
import { CheckCircle2, XCircle, Loader2, ShieldCheck } from "lucide-react";

export default function KYCSection({ app, customer, reload }) {
  const [form, setForm] = useState({ aadhaar_last4: customer.aadhaar_last4 || "", pan: customer.pan || "", gstin: customer.gstin || "", cibil_score: customer.cibil_score || "" });
  const [busy, setBusy] = useState({});
  const [results, setResults] = useState({});
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const verify = async (kind) => {
    setBusy((b) => ({ ...b, [kind]: true }));
    try {
      let res;
      if (kind === "aadhaar") res = await api.post("/integrations/aadhaar/verify", { aadhaar_number: form.aadhaar_last4 });
      else if (kind === "pan") res = await api.post("/integrations/pan/verify", { pan: form.pan });
      else if (kind === "gst") res = await api.post("/integrations/gst/verify", { gstin: form.gstin });
      else if (kind === "cibil") res = await api.post("/integrations/cibil/pull", { pan: form.pan, name: customer.name });
      setResults((r) => ({ ...r, [kind]: res.data }));
      // Persist the relevant field on customer
      const patch = {};
      if (kind === "aadhaar") patch.aadhaar_last4 = form.aadhaar_last4;
      if (kind === "pan") patch.pan = form.pan;
      if (kind === "gst") patch.gstin = form.gstin;
      if (kind === "cibil") patch.cibil_score = res.data?.score;
      if (Object.keys(patch).length) {
        await api.patch(`/customers/${customer.id}`, patch);
      }
      toast.success(`${kind.toUpperCase()} verified (mocked)`);
      reload && reload();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Verification failed");
    } finally {
      setBusy((b) => ({ ...b, [kind]: false }));
    }
  };

  return (
    <div className="space-y-3">
      <SectionHeader title="KYC Verification" subtitle={<span className="flex items-center gap-1"><Badge variant="outline" className="text-[10px]">MOCKED</Badge> External APIs are simulated for V1.</span>} />
      <Card className="p-5 space-y-4">
        <Row label="Aadhaar (last 4)" placeholder="6789" value={form.aadhaar_last4} onChange={(v) => set("aadhaar_last4", v)} onClick={() => verify("aadhaar")} busy={busy.aadhaar} result={results.aadhaar} testid="detail-kyc-aadhaar" />
        <Row label="PAN" placeholder="ABCDE1234F" value={form.pan} onChange={(v) => set("pan", v.toUpperCase())} onClick={() => verify("pan")} busy={busy.pan} result={results.pan} testid="detail-kyc-pan" />
        <Row label="GSTIN" placeholder="12ABCDE1234F1Z5" value={form.gstin} onChange={(v) => set("gstin", v.toUpperCase())} onClick={() => verify("gst")} busy={busy.gst} result={results.gst} testid="detail-kyc-gst" />
        <div className="flex items-end gap-2">
          <div className="flex-1">
            <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold mb-1">CIBIL Score</div>
            <Input type="number" value={form.cibil_score} onChange={(e) => set("cibil_score", e.target.value)} />
          </div>
          <Button variant="secondary" onClick={() => verify("cibil")} disabled={busy.cibil} data-testid="detail-kyc-cibil-pull">{busy.cibil ? <Loader2 className="h-4 w-4 animate-spin" /> : "Pull CIBIL"}</Button>
        </div>
        {results.cibil && (
          <div className="rounded-md border p-3 text-xs bg-muted/30">
            <div className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-success" /><span className="font-medium">CIBIL Snapshot</span> <Badge variant="outline" className="text-[10px]">MOCKED</Badge></div>
            <div className="mt-2 grid grid-cols-2 md:grid-cols-4 gap-2">
              <Mini k="Score" v={results.cibil.score} />
              <Mini k="Band" v={results.cibil.score_band} />
              <Mini k="Active a/c" v={results.cibil.active_accounts} />
              <Mini k="Oldest a/c" v={`${results.cibil.oldest_account_years} yrs`} />
            </div>
          </div>
        )}
        <div className="pt-2 border-t border-border space-y-2">
          <div className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Attach KYC Documents</div>
          <ContextualUploader applicationId={app.id} category="KYC" title="Aadhaar / PAN / Passport / Voter ID" multiple />
        </div>
      </Card>
    </div>
  );
}

function Row({ label, value, placeholder, onChange, onClick, busy, result, testid }) {
  return (
    <div className="space-y-1.5">
      <div className="text-xs uppercase tracking-wide text-muted-foreground font-semibold">{label}</div>
      <div className="flex gap-2">
        <Input value={value} placeholder={placeholder} onChange={(e) => onChange(e.target.value)} data-testid={`${testid}-input`} />
        <Button variant="secondary" onClick={onClick} disabled={busy} data-testid={`${testid}-button`}>{busy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Verify"}</Button>
      </div>
      {result && (
        <div className="text-xs flex items-center gap-2 text-muted-foreground">
          {(result.status === "verified" || result.status === "active" || result.status === "success") ? <CheckCircle2 className="h-3.5 w-3.5 text-success" /> : <XCircle className="h-3.5 w-3.5 text-destructive" />}
          <span className="mono">{result.provider}</span> · {result.status}
          {result.name && <> · Name: {result.name}</>}
          {result.legal_name && <> · Legal Name: {result.legal_name}</>}
        </div>
      )}
    </div>
  );
}

function Mini({ k, v }) {
  return (
    <div>
      <div className="text-[10px] uppercase text-muted-foreground">{k}</div>
      <div className="text-sm font-semibold mono">{v}</div>
    </div>
  );
}
